import React, { forwardRef, useImperativeHandle, useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { Box, Divider, FormGroup, Heading } from "@hawkins/components";
import { Dimensions } from "@hawkins/variables";
import {
  DatePickerDropdownFormControl,
  InputFormControl,
  FormContextualErrors,
  FormProvider,
  useForm,
} from "@hawkins/forms";

import { blankEmergencyForm, TranslationNamespace, EmergencyForm } from "../../types";
import { EmergencyFormSchema } from '../../utils/schemas';
import { SectionHeader, FieldHeader } from "../fields";
import { MOPRepeater, EmailRepeater, PhoneRepeater } from "../fields";
import { Signature } from "./Signature";
import { useSession } from "../../hooks/useSessionContext";


export interface EmergencyFormProps {
  onSubmit: (formValues: EmergencyForm) => void;
  onFormStateChange?: (isValid: boolean, signatureAgreement: boolean) => void;
}

export interface EmergencyFormRef {
  reset: () => void;
  submit: () => void;
}

export const EmergencyFormComponent = forwardRef<EmergencyFormRef, EmergencyFormProps>(
  ({ onSubmit, onFormStateChange }, ref) => {
    const { t } = useTranslation(TranslationNamespace.LEP);
    const { userInfo } = useSession();

    const schema = EmergencyFormSchema(t);
    type FormSchema = z.infer<typeof schema>;
    const methods = useForm<FormSchema>({
      defaultValues: blankEmergencyForm as any,
      mode: 'onChange',
      resolver: zodResolver(schema),
    });

    const { watch, formState, setValue } = methods;
    const signatureAgreement = watch('signatureAgreement');

    useEffect(() => {
      setValue("signatureToMatch", `${userInfo?.givenName} ${userInfo?.familyName}`);
    }, [setValue, userInfo]);

    React.useEffect(() => {
      onFormStateChange?.(formState.isValid, signatureAgreement);
    }, [formState.isValid, signatureAgreement, onFormStateChange]);

    const handleSubmit = methods.handleSubmit(
      (formValues) => onSubmit(formValues as any),
      (error, evt) => console.info(error, evt),
    );

    useImperativeHandle(ref, () => ({
      reset: () => methods.reset(),
      submit: handleSubmit,
    }));
      
    const fieldLabels = useMemo(() => ({
      "memberIdPayload.identifierPayloads": t("other_account_header"),
      "memberIdPayload.identifierPayloads.mop": t("mop_identifier"),
      "memberIdPayload.identifierPayloads.routingNumber": t("mop_type7_field1"),
      "memberIdPayload.identifierPayloads.accountNumber": t("mop_type7_field2"),
      "memberIdPayload.identifierPayloads.branchNumber": t("mop_type8_field1"),
      "memberIdPayload.identifierPayloads.digit": t("mop_type8_field2"),
      "memberIdPayload.emailAddresses": t("field_email_address"),
      "memberIdPayload.phoneNumbers": t("emer_field1_option3"),
      "eventDate": t("emer_field2_label"),
      "signature": t("stolen_section_d_title"),
    }), [t]);

    return (
      <FormProvider {...methods}>
        <FormGroup>
          <FormContextualErrors fieldLabels={fieldLabels} />
          <Box rowGap={Dimensions.Space16} display="grid">
            <Heading level={5}>{t("emer_title")}</Heading>

            <SectionHeader title="other_account_header" desc="netflix_identifier_desc">

              <FieldHeader title="mop_identifier" desc="MOP_EXPLANATION" />
              <MOPRepeater methods={methods} />

              <FieldHeader title="field_email_address" desc="email_explanation" />
              <EmailRepeater methods={methods} />

              <FieldHeader title="emer_field1_option3" desc="phone_explanation" />
              <PhoneRepeater methods={methods} />

              <InputFormControl
                name={`memberIdPayload.other`}
                label={t("inv_type_other")}
                description={t("form_identifier_other_description")}
                style={{ width: '100%' }}
                rows={5}
                multiline
                data-testid="other-identifier-input"
              />
              <Divider />

              <Box width={'40%'}>
                <DatePickerDropdownFormControl<FormSchema>
                  name="eventDate"
                  label={t("emer_field2_label")}
                  description={t("emer_field2_desc")}
                  style={{ width: '50%' }}
                  closeDropdownOnSelectedChange
                  data-testid="event-date-picker"
                  excludeDate={(date: string) => new Date(date) > new Date()}
                />
              </Box>
            </SectionHeader>

            <SectionHeader title="emer_section_b_title" desc="emer_section_b_desc">
              <Box rowGap={Dimensions.Space24} display="grid" paddingTop={Dimensions.Space24}>
                <InputFormControl
                  name={`description1`}
                  label={t("emer_question1")}
                  requirementType="required-star"
                  style={{ width: '100%' }}
                  rows={5}
                  multiline
                  data-testid="description1-input"
                />
                <InputFormControl
                  name={`description2`}
                  label={t("emer_question2")}
                  requirementType="required-star"
                  style={{ width: '100%' }}
                  rows={5}
                  multiline
                  data-testid="description2-input"
                />
                <InputFormControl
                  name={`description3`}
                  label={t("emer_question3")}
                  requirementType="required-star"
                  style={{ width: '100%' }}
                  rows={5}
                  multiline
                  data-testid="description3-input"
                />
                <InputFormControl
                  name={`description4`}
                  label={t("emer_question4")}
                  requirementType="required-star"
                  style={{ width: '100%' }}
                  rows={5}
                  multiline
                  data-testid="description4-input"
                />
              </Box>
            </SectionHeader>

            <Box backgroundColor={"white"} padding={Dimensions.Space16}>
              <SectionHeader title="mop_section_c_title">
                <Signature />
              </SectionHeader>
            </Box>
          </Box>
        </FormGroup>
      </FormProvider>
    );
  }
)
